/** The fields of an attachment record this module needs. Structurally satisfied
 * by `SubmissionAttachment` from `@rubi/api-generated`, without depending on it. */
export type AttachmentRecord = {
  id: string;
  filename: string;
};

type SavedFileItem = { name: string; id?: string; type?: string };

const isSavedFileItem = (item: unknown): item is SavedFileItem => {
  if (!item || typeof item !== "object") return false;
  const candidate = item as SavedFileItem;
  return (
    typeof candidate.name === "string" &&
    (typeof candidate.id === "string" || typeof candidate.type === "string")
  );
};

/**
 * Rebuild the `questionName + filename` → attachment id map that report pages
 * keep while a submission is being filled out or edited, from its saved data.
 *
 * The prefix matters: every write (the `onUploadFiles` handler) and every read
 * (the `onClearFiles` handler, and the duplicate-filename rename's `usedNames`)
 * keys on question name + filename. Rebuilding with any other scheme leaves
 * entries that removal can never delete, so the attachment stays in
 * `attachments.ids`, keeps `uploaded = true`, and is still exported.
 *
 * Our enhanced upload question records the attachment id alongside each file, so
 * prefixes resolve by id first. SurveyJS's built-in file question keeps only
 * `{name, type, content}`, hence the filename fallback. Attachments the survey
 * data doesn't account for (a generated PDF, say) keep a bare filename key so
 * their ids stay in `attachments.ids` rather than being swept as orphans.
 */
export const buildAttachmentIdMap = (
  submissionData: unknown,
  attachments: ReadonlyArray<AttachmentRecord>,
): { [key: string]: string } => {
  const prefixByAttachmentId = new Map<string, string>();
  const prefixByFilename = new Map<string, string>();

  const visit = (node: unknown) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    // A file question's answer is an array of file items stored under the
    // question name — which is exactly the prefix used when uploading.
    for (const [questionName, value] of Object.entries(node)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (!isSavedFileItem(item)) continue;
          if (item.id) {
            prefixByAttachmentId.set(item.id, questionName);
          }
          if (!prefixByFilename.has(item.name)) {
            prefixByFilename.set(item.name, questionName);
          }
        }
      }
      visit(value);
    }
  };
  visit(submissionData);

  const attachmentIDs: { [key: string]: string } = {};
  for (const attachment of attachments) {
    const prefix =
      prefixByAttachmentId.get(attachment.id) ??
      prefixByFilename.get(attachment.filename) ??
      "";
    attachmentIDs[prefix + attachment.filename] = attachment.id;
  }
  return attachmentIDs;
};
